import React, { useEffect, useRef, useState, useCallback } from "react";
import "../Style/appservices.css";

import app1 from "../assets/icon1.png";
import app2 from "../assets/icon2.png";
import app3 from "../assets/icon3.png";
import app4 from "../assets/icon4.png";
import app5 from "../assets/icon5.png";
import app6 from "../assets/icon6.png";

const services = [
  {
    icon: app1,
    title: "Custom Mobile App Development",
    desc: "We create custom mobile applications designed around your business requirements, delivering scalable architecture, secure functionality, and seamless user experiences that support long-term business growth.",
    tags: [
      "Custom Mobile Apps",
      "Business Applications",
      "Secure Development",
      "API Integration",
      "Scalable Architecture",
    ],
  },
  {
    icon: app2,
    title: "Cross-Platform & Native Development",
    desc: "Our developers build high-performance Android, iOS, Flutter, and React Native applications that deliver native-like performance, faster development cycles, and consistent experiences across multiple devices.",
    tags: [
      "Android Development",
      "iOS Development",
      "Flutter Development",
      "React Native",
      "Cross-Platform Apps",
    ],
  },
  {
    icon: app3,
    title: "Intuitive App UI/UX Design",
    desc: "Our UI/UX designers create intuitive, engaging, and visually appealing interfaces that improve usability, increase user retention, and provide seamless interactions across every screen.",
    tags: [
      "Mobile UI Design",
      "UX Research",
      "Wireframing",
      "User Experience",
      "Design Systems",
    ],
  },
  {
    icon: app4,
    title: "App Performance & Scalability",
    desc: "We optimize mobile applications for speed, stability, and scalability, ensuring smooth performance even under heavy traffic while maintaining exceptional reliability and responsiveness.",
    tags: [
      "High Performance",
      "App Optimization",
      "Load Balancing",
      "Scalable Infrastructure",
      "Performance Testing",
    ],
  },
  {
    icon: app5,
    title: "Third-Party API Integration",
    desc: "We integrate secure APIs and third-party services including payment gateways, cloud platforms, authentication systems, analytics, maps, messaging, and CRM tools to extend your application's capabilities.",
    tags: [
      "API Integration",
      "Payment Gateway",
      "Cloud Services",
      "Firebase",
      "Push Notifications",
    ],
  },
  {
    icon: app6,
    title: "Offline Functionality & Data Sync",
    desc: "We build mobile applications with offline capabilities, allowing users to continue using essential features without internet access while automatically synchronizing data once connectivity is restored.",
    tags: [
      "Offline Mode",
      "Data Sync",
      "Local Storage",
      "Background Sync",
      "Seamless Connectivity",
    ],
  },
];

const AUTOPLAY_DELAY = 4500;

function ServicesSection() {
  const sectionRef = useRef(null);
  const headingRef = useRef(null);
  const subtextRef = useRef(null);
  const autoplayRef = useRef(null);

  const [sectionVisible, setSectionVisible] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const total = services.length;

  // Scroll-reveal for heading/subtext
  useEffect(() => {
    const targets = [headingRef.current, subtextRef.current].filter(Boolean);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setSectionVisible(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.15 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Autoplay
  useEffect(() => {
    if (isPaused) return;
    autoplayRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % total);
    }, AUTOPLAY_DELAY);
    return () => clearInterval(autoplayRef.current);
  }, [isPaused, total]);

  const goTo = useCallback(
    (index) => {
      setActiveIndex(((index % total) + total) % total);
    },
    [total]
  );

  const goPrev = () => goTo(activeIndex - 1);
  const goNext = () => goTo(activeIndex + 1);

  // Position of each card relative to the active one (-1, 0, 1, or "far")
  const getPosition = (index) => {
    let diff = index - activeIndex;
    if (diff > total / 2) diff -= total;
    if (diff < -total / 2) diff += total;

    if (diff === 0) return "active";
    if (diff === -1) return "prev";
    if (diff === 1) return "next";
    return "far";
  };

  return (
    <section
      className="appservices-services-section"
      ref={sectionRef}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <span className="appservices-services-watermark" aria-hidden="true">
        SERVICES
      </span>

      <span
        className={`appservices-services-eyebrow ${
          sectionVisible ? "appservices-reveal" : ""
        }`}
      >
        What We Offer
      </span>

      <h2
        className={`appservices-services-heading ${
          sectionVisible ? "appservices-reveal" : ""
        }`}
        ref={headingRef}
      >
        App Solutions for Your Digital Growth
      </h2>

      <p
        className={`appservices-services-subtext ${
          sectionVisible ? "appservices-reveal" : ""
        }`}
        ref={subtextRef}
      >
        We develop secure, scalable, and feature-rich mobile applications
        tailored to your business goals. From strategy and UI/UX design to
        development, API integration, testing, deployment, and maintenance,
        our end-to-end mobile app development services ensure your
        application delivers outstanding performance, security, and user
        engagement.
      </p>

      <div className="appservices-carousel">
        <button
          type="button"
          className="appservices-carousel-arrow appservices-carousel-arrow-left"
          onClick={goPrev}
          aria-label="Previous service"
        >
          ‹
        </button>

        <div className="appservices-carousel-track">
          {services.map((service, index) => {
            const position = getPosition(index);
            return (
              <div
                className={`appservices-service-card is-${position}`}
                key={service.title}
                onClick={() => position !== "active" && goTo(index)}
              >
                <div className="appservices-service-icon-wrap">
                  <img
                    src={service.icon}
                    alt={service.title}
                    className="appservices-service-icon"
                  />
                </div>

                <h3>{service.title}</h3>
                <p>{service.desc}</p>

                <div className="appservices-service-tags">
                  {service.tags.slice(0, 3).map((tag, i) => (
                    <span key={i}>{tag}</span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <button
          type="button"
          className="appservices-carousel-arrow appservices-carousel-arrow-right"
          onClick={goNext}
          aria-label="Next service"
        >
          ›
        </button>
      </div>

      <div className="appservices-carousel-dots">
        {services.map((_, index) => (
          <button
            key={index}
            type="button"
            className={`appservices-dot ${
              index === activeIndex ? "is-active" : ""
            }`}
            onClick={() => goTo(index)}
            aria-label={`Go to service ${index + 1}`}
          />
        ))}
      </div>
    </section>
  );
}

export default ServicesSection;